import { useUiModelStore } from "@agent/uiModelStore";
import { queryClient } from "@api/queryClient";
import { useWriteErrorStore } from "@api/writeErrorStore";
import { usePrincipalKey } from "@auth/sessionStore";
import { useEffect, useRef } from "react";

/**
 * Clears per-user state whenever the signed-in user changes: on a shared clinic device a
 * user switch must not keep serving views, subject lookups or other per-role data fetched
 * under the previous user.
 *
 * Unsubmitted a2ui form input goes too: its keys are already principal-scoped, so
 * this is about not retaining typed clinical text once its author is gone.
 *
 * The write-failure notice is dismissed as well. It has no auto-timeout, so without
 * this the previous user's "You don’t have permission to…" would sit on screen for
 * whoever signs in next, describing an action they never attempted.
 *
 * Keyed on the whole principal, not just the user, so re-pairing a device to another
 * clinic resets as well. Token rotation keeps the same principal and so doesn't fire.
 *
 * The initial session mount is skipped, so this fires only on a real user switch.
 */
export const SessionCacheReset = () => {
  const sessionKey = usePrincipalKey();
  const isInitialSession = useRef(true);

  useEffect(() => {
    if (isInitialSession.current) {
      isInitialSession.current = false;
      return;
    }
    queryClient.clear();
    useUiModelStore.getState().clearAll();
    useWriteErrorStore.getState().dismiss();
  }, [sessionKey]);

  return null;
};
